import { useEffect, useCallback, useRef, type MouseEvent, type ReactNode } from 'react';
import { type RelayProp, graphql, createFragmentContainer } from 'react-relay';
import { connect, type DispatchProp } from 'react-redux';

import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';

import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';
import makeOfferLauncher from 'dibs-buyer-layout/exports/makeOfferLauncher.jsx';
import { SharedModifyPortfolioItems } from '../../mutations/FindingSharedModifyPortfolioItems.ts';

import styles from './PdpSharedButtons.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { redirectToOMT } from './utils.ts';
import { type Maybe } from 'dibs-ts-utils/exports/Maybe.ts';
import {
    trackMakeOfferClick,
    trackMakeOfferFromEmail,
    locations,
    type Location,
} from '../tracking/actionButtonsTracking.ts';
import { setCartItems } from '../../actions/sharedMobileActions.js';
import { updateMakeOfferModalShown } from '../../actions/pdpRespMakeOfferModalActions.js';
import { getQueryParam } from '../../utils/uriUtils.ts';
import { isMultiItemCheckoutVariant } from 'dibs-checkout/exports/useMultiItemCheckout.ts';

import { type PdpSharedMakeOfferButton_item$data } from './__generated__/PdpSharedMakeOfferButton_item.graphql.ts';
import { type PdpSharedMakeOfferButton_user$data } from './__generated__/PdpSharedMakeOfferButton_user.graphql.ts';

type Props = {
    userId?: string | null;
    itemId: string;
    selectedSkuId?: string;
    item: PdpSharedMakeOfferButton_item$data;
    currency: string;
    relay: RelayProp;
    user: PdpSharedMakeOfferButton_user$data | null | undefined;
    quantitySelected?: string;
    gaLabel?: string;
    location?: Location;
    className?: string;
    size?: 'small' | 'medium' | 'large';
    showArrow?: boolean;
    hasPrivateOffer?: Maybe<boolean>;
    shouldLaunchContactDealer?: boolean;
} & DispatchProp;

function getButtonType({
    isPrivateVisibilityType,
}: {
    isPrivateVisibilityType: boolean | null | undefined;
}): 'secondaryAlt' | 'secondary' {
    if (isPrivateVisibilityType) {
        return 'secondaryAlt';
    }
    return 'secondary';
}

let makeOfferModalShown = false;

function PdpSharedMakeOfferButtonComponentFn({
    dispatch,
    userId,
    itemId,
    selectedSkuId,
    item,
    currency,
    relay,
    quantitySelected,
    gaLabel,
    location,
    className,
    size = 'large',
    showArrow = true,
    hasPrivateOffer = false,
    shouldLaunchContactDealer = true,
}: Props): ReactNode {
    const isModalOpenRef = useRef(false);

    const onClick = useCallback(
        (event?: MouseEvent) => {
            const { makeOfferVpvParams: pdpTrackingVpvParams, ...itemRest } = item;
            const isMultiItemCheckout = isMultiItemCheckoutVariant();

            trackMakeOfferClick({
                item: { ...itemRest, pdpTrackingVpvParams },
                hasPrivateOffer,
                event,
                location,
                environment: relay.environment,
                quantity: Number(quantitySelected),
            });
            if (!isMultiItemCheckout) {
                if (!userId) {
                    dispatch(setCartItems(itemId));
                } else {
                    SharedModifyPortfolioItems.commit(relay.environment, {
                        action: 'ADD',
                        itemId,
                        portfolioType: 'CART',
                        userId,
                    });
                }
            }

            const { serviceId: transactionId, hasCompletedCheckout } =
                itemRest?.lastActiveTransaction || {};

            if (hasPrivateOffer && transactionId && hasCompletedCheckout) {
                redirectToOMT(transactionId);
            } else {
                isModalOpenRef.current = true;
                makeOfferModalShown = true;
                makeOfferLauncher({
                    itemId,
                    selectedSkuId,
                    currency,
                    environment: relay.environment,
                    quantitySelected,
                    shouldLaunchContactDealer,
                    launchOrigin: 'pdp-shared',
                    gaLabel,
                    page: 'pdp',
                    onClose: () => dispatch(updateMakeOfferModalShown(false)),
                    onOpen: () => dispatch(updateMakeOfferModalShown(true)),
                });
            }
        },
        [
            dispatch,
            userId,
            itemId,
            selectedSkuId,
            item,
            currency,
            relay,
            quantitySelected,
            gaLabel,
            location,
            hasPrivateOffer,
            shouldLaunchContactDealer,
        ]
    );

    useEffect(() => {
        const queryParams = global?.location?.search;
        const modalParam = getQueryParam(queryParams, 'modal');
        const templateParam = getQueryParam(queryParams, 'utm_campaign');
        if (modalParam === 'offer' && !makeOfferModalShown) {
            if (!isModalOpenRef.current) {
                onClick();
            }

            if (templateParam) {
                trackMakeOfferFromEmail(templateParam, {
                    pdpTrackingVpvParams: item.makeOfferVpvParams,
                });
            }
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isPrivateVisibilityType = item?.isPrivateVisibilityType;
    const offerButton =
        !isPrivateVisibilityType && location !== locations.SIMILAR_SOLD ? (
            <FormattedMessage
                id="PdpSharedMakeOfferButton.suggestPriceCta"
                defaultMessage="Suggest a Price"
            />
        ) : (
            <FormattedMessage
                id="PdpSharedMakeOfferButton.makeOfferCta"
                defaultMessage="Make an Offer"
            />
        );

    return (
        <Button
            type={getButtonType({ isPrivateVisibilityType })}
            className={classnames(styles.button, className)}
            dataTn="item-actions-make-offer"
            onClick={onClick}
            size={size}
            fullWidth
        >
            <div className={classnames(dibsCss.flex, dibsCss.justifyCenter, dibsCss.relative)}>
                {hasPrivateOffer ? (
                    <FormattedMessage
                        id="PdpSharedMakeOfferButton.counterOffer"
                        defaultMessage="Counter Offer"
                    />
                ) : (
                    offerButton
                )}
                {showArrow && size !== 'small' && <ArrowRight className={styles.arrowSVG} />}
            </div>
        </Button>
    );
}

export const PdpSharedMakeOfferButtonComponent = connect()(PdpSharedMakeOfferButtonComponentFn);

export const PdpSharedMakeOfferButton = createFragmentContainer(PdpSharedMakeOfferButtonComponent, {
    item: graphql`
        fragment PdpSharedMakeOfferButton_item on Item {
            ...SharedContactDealerWrapper_item
            ...actionButtonsTracking_item
            makeOfferVpvParams: pdpTrackingVpvParams(
                userId: $userId
                excludedPurchaseStatuses: [RET, CUST]
            ) @include(if: $afterDisplay)
            isPrivateVisibilityType
            lastActiveTransaction(buyerId: $userId) {
                serviceId
                hasCompletedCheckout
            }
        }
    `,
    user: graphql`
        fragment PdpSharedMakeOfferButton_user on User {
            ...SharedContactDealerWrapper_user
        }
    `,
});
